// Push notifications helpers — subscribe / unsubscribe / status.
// El SW se registra desde otro lado (probablemente ya está en index.js).
// Este archivo solo maneja la subscripción a la Push API + registro
// contra el Worker.

const WORKER_BASE = (
  process.env.REACT_APP_PAPI_WORKER_URL
  || "https://preguntale-a-papi.guendelman.workers.dev"
).replace(/\/ask-papi\/?$/, "").replace(/\/+$/, "");

// VAPID public key (misma que en el Worker) — se pasa a pushManager.subscribe.
const VAPID_PUBLIC_KEY = "BKaZR-1up30puNL5aYOWR1SNv-UYVo7ngNiCr78CSfAPxRdOKhWMeFBc-XOh0mvTshlkHiW_Vj3pb4hEARURSz4";

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - b64.length % 4) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported() {
  return (
    typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window
  );
}

export async function currentSubscription() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch { return null; }
}

// ¿La suscripción fue creada con la clave pública actual? (si cambiamos las claves VAPID, no)
export function subscriptionKeyMatches(sub) {
  const k = sub && sub.options && sub.options.applicationServerKey;
  if (!k) return true;
  const a = new Uint8Array(k), b = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

export async function getOrCreateSubscription() {
  if (!pushSupported()) throw new Error("Push no soportado en este navegador");
  const perm = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permiso denegado");
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (sub && !subscriptionKeyMatches(sub)) {
    await sub.unsubscribe();
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }
  return sub;
}

export async function subscribePush(member, displayName) {
  const sub = await getOrCreateSubscription();
  // Registrar contra el Worker
  const res = await fetch(`${WORKER_BASE}/subscribe-push`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      member,
      displayName,
      subscription: sub.toJSON(),
    }),
  });
  const json = await res.json();
  if (!res.ok || !json.ok) throw new Error(json.error || "subscribe_failed");
  localStorage.setItem(`push_subscribed_${member}`, "1");
  return sub;
}

export async function unsubscribePush(member) {
  const sub = await currentSubscription();
  if (!sub) {
    localStorage.removeItem(`push_subscribed_${member}`);
    return;
  }
  try {
    await fetch(`${WORKER_BASE}/unsubscribe-push`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint: sub.endpoint, member }),
    });
  } catch { /* ignore */ }
  try { await sub.unsubscribe(); } catch { /* ignore */ }
  localStorage.removeItem(`push_subscribed_${member}`);
}

export function isSubscribedLocally(member) {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(`push_subscribed_${member}`) === "1";
}

// Marca que el user descartó el prompt — no lo volvemos a mostrar hasta
// que cambie de miembro o pase 30 días.
export function dismissPrompt(member) {
  if (typeof localStorage === "undefined") return;
  const key = `push_prompt_dismissed_${member}`;
  localStorage.setItem(key, String(Date.now()));
}
export function promptDismissedRecently(member) {
  if (typeof localStorage === "undefined") return false;
  const key = `push_prompt_dismissed_${member}`;
  const raw = localStorage.getItem(key);
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  if (isNaN(ts)) return false;
  return (Date.now() - ts) < 30 * 24 * 3600 * 1000; // 30 días
}
